import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import type { Reel } from '@/shared/types';
import { SmartImage } from '@/shared/ui/SmartImage';

type Props = {
  reel: Reel;
  isActive: boolean;
  children?: ReactNode;
};

/**
 * Active-only video budget: only the active reel mounts a <video>.
 * Inactive reels render a poster <img> of identical intrinsic size to
 * keep memory and decoder pressure bounded inside the WebView.
 *
 * Shows a centered spinner while the active video is buffering (waiting,
 * stalled, or before first frame). Hidden as soon as playback resumes.
 */
export function ReelItem({ reel, isActive, children }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Assume we're buffering until the video signals it can play.
  const [isBuffering, setIsBuffering] = useState(true);

  useEffect(() => {
    if (!isActive && videoRef.current) videoRef.current.pause();
    // Reset buffering state when we become inactive — next time this reel
    // mounts a video, we want the spinner to start showing again.
    if (!isActive) setIsBuffering(true);
  }, [isActive]);

  return (
    <div className="relative h-full w-full snap-start bg-black">
      {isActive ? (
        <video
          ref={videoRef}
          src={reel.video}
          poster={reel.poster}
          autoPlay
          muted
          loop
          playsInline
          onWaiting={() => setIsBuffering(true)}
          onStalled={() => setIsBuffering(true)}
          onCanPlay={() => setIsBuffering(false)}
          onPlaying={() => setIsBuffering(false)}
          className="h-full w-full object-cover"
        />
      ) : (
        <SmartImage src={reel.poster} fill aspect="9/16" alt={reel.caption} />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
      {/* Buffering indicator — temporarily tucked into the corner at near-zero
          opacity (was a centered 36px spinner; users found it intrusive). The
          code path is intact so we can re-enable / resize quickly later. */}
      {isActive && isBuffering && (
        <div
          data-testid="reel-buffering"
          className="absolute top-3 left-3 pointer-events-none"
          aria-hidden="true"
        >
          <Loader2 className="w-3 h-3 text-white/15 animate-spin" />
        </div>
      )}
      {children}
    </div>
  );
}
